import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Button, Badge, Alert, Modal, Form, Card } from 'react-bootstrap';
import axios from 'axios';

interface UpgradeRequest {
  _id: string;
  user?: {
    _id: string;
    name: string;
    email: string;
    userType: string;
  };
  currentUserType: string;
  requestedUserType: string;
  transactionId: string;
  amountPaid: number;
  paymentMethod: string;
  paymentDate: string;
  status: 'pending' | 'approved' | 'rejected';
  adminResponse?: string;
  reviewedBy?: {
    name: string;
    email: string;
  };
  reviewedAt?: string;
  submittedAt: string;
  notes?: string;
}

const UpgradeManagement: React.FC = () => {
  const [requests, setRequests] = useState<UpgradeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UpgradeRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [adminResponse, setAdminResponse] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const loadUpgradeRequests = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(currentPage));
      queryParams.set('limit', String(pageSize));
      if (statusFilter !== 'all') {
        queryParams.set('status', statusFilter);
      }
      const params = `?${queryParams.toString()}`;
      
      const response = await axios.get(`${API_BASE_URL}/admin/upgrade-requests${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRequests(response.data.requests);
        setTotalRequests(response.data.pagination?.totalRecords || 0);
        setTotalPages(response.data.pagination?.total || 1);
        calculateStats(response.data.requests);
      }
    } catch (error: any) {
      console.error('Error loading upgrade requests:', error);
      setError(error.response?.data?.message || 'Failed to load upgrade requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, API_BASE_URL, currentPage, pageSize]);

  useEffect(() => {
    loadUpgradeRequests();
  }, [loadUpgradeRequests]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const calculateStats = (allRequests: UpgradeRequest[]) => {
    const stats = {
      total: allRequests.length,
      pending: allRequests.filter(r => r.status === 'pending').length,
      approved: allRequests.filter(r => r.status === 'approved').length,
      rejected: allRequests.filter(r => r.status === 'rejected').length
    };
    setStats(stats);
  };

  const handleAction = async (request: UpgradeRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminResponse('');
    setShowModal(true);
  };

  const submitAction = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const endpoint = `${API_BASE_URL}/admin/upgrade-requests/${selectedRequest._id}/${actionType}`;
      
      const response = await axios.patch(endpoint, {
        response: adminResponse
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(`Upgrade request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setShowModal(false);
        loadUpgradeRequests(); // Reload data
      }
    } catch (error: any) {
      console.error(`Error ${actionType}ing request:`, error);
      setError(error.response?.data?.message || `Failed to ${actionType} request`);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getUserTypeBadge = (userType: string) => {
    const variants: { [key: string]: string } = {
      free: 'secondary',
      pro: 'warning',
      admin: 'danger'
    };
    const type = userType || 'free';
    return <Badge bg={variants[type] || 'secondary'}>{type.toUpperCase()}</Badge>;
  };

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-rocket me-2"></i>
              Upgrade Request Management
            </h5>
          </Col>
          <Col xs="auto">
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="all">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="admin-card-body">
        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-primary mb-2">
                  <i className="fas fa-clipboard-list fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.total}</h4>
                <small className="text-muted">Total Requests</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-warning mb-2">
                  <i className="fas fa-clock fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.pending}</h4>
                <small className="text-muted">Pending</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-success mb-2">
                  <i className="fas fa-check-circle fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.approved}</h4>
                <small className="text-muted">Approved</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-danger mb-2">
                  <i className="fas fa-times-circle fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.rejected}</h4>
                <small className="text-muted">Rejected</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
            <p>Loading upgrade requests...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Current → Requested</th>
                  <th>Transaction Details</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                      <p className="text-muted mb-0">No upgrade requests found</p>
                    </td>
                  </tr>
                ) : (
                  requests
                    .map(request => (
                    <tr key={request._id}>
                      <td>
                        <div>
                          <strong>{request.user?.name || 'Unknown User'}</strong>
                          <br />
                          <small className="text-muted">{request.user?.email || 'No email'}</small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getUserTypeBadge(request.currentUserType)}
                          <i className="fas fa-arrow-right mx-2 text-muted"></i>
                          {getUserTypeBadge(request.requestedUserType)}
                        </div>
                      </td>
                      <td>
                        <div>
                          <code className="text-primary">{request.transactionId}</code>
                          <br />
                          <small className="text-muted">
                            {request.paymentMethod.replace('_', ' ').toUpperCase()}
                          </small>
                          <br />
                          <small className="text-muted">
                            {new Date(request.paymentDate).toLocaleDateString()}
                          </small>
                        </div>
                      </td>
                      <td>
                        <strong className="text-success">₹{request.amountPaid}</strong>
                      </td>
                      <td>
                        {getStatusBadge(request.status)}
                        {request.reviewedBy && (
                          <div>
                            <small className="text-muted">
                              by {request.reviewedBy.name}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        <small>{new Date(request.submittedAt).toLocaleString()}</small>
                        {request.reviewedAt && (
                          <div>
                            <small className="text-muted">
                              Reviewed: {new Date(request.reviewedAt).toLocaleString()}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        {request.status === 'pending' ? (
                          <div className="btn-group" role="group">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleAction(request, 'approve')}
                              title="Approve Request"
                            >
                              <i className="fas fa-check"></i>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleAction(request, 'reject')}
                              title="Reject Request"
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <small className="text-muted">
                              {request.status === 'approved' ? 'Approved' : 'Rejected'}
                            </small>
                            {request.adminResponse && (
                              <div>
                                <small className="text-muted" title={request.adminResponse}>
                                  <i className="fas fa-comment"></i> Response given
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            {totalRequests > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRequests)} of {totalRequests} requests
                  </small>
                </div>
                <div className="btn-group">
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <Button variant="primary" disabled>
                    {currentPage}
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`fas ${actionType === 'approve' ? 'fa-check text-success' : 'fa-times text-danger'} me-2`}></i>
            {actionType === 'approve' ? 'Approve' : 'Reject'} Upgrade Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className="mb-3 p-3 bg-light rounded">
                <strong>User:</strong> {selectedRequest.user?.name || 'Unknown User'} ({selectedRequest.user?.email || 'No email'})
                <br />
                <strong>Transaction ID:</strong> <code>{selectedRequest.transactionId}</code>
                <br />
                <strong>Amount:</strong> ₹{selectedRequest.amountPaid}
                <br />
                <strong>Payment Method:</strong> {selectedRequest.paymentMethod.replace('_', ' ').toUpperCase()}
                {selectedRequest.notes && (
                  <>
                    <br />
                    <strong>User Notes:</strong> {selectedRequest.notes}
                  </>
                )}
              </div>

              <Form.Group>
                <Form.Label>Admin Response {actionType === 'reject' && <span className="text-danger">*</span>}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder={
                    actionType === 'approve'
                      ? 'Optional message to the user (e.g., "Welcome to Pro!")'
                      : 'Please provide a reason for rejection...'
                  }
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {adminResponse.length}/500 characters
                  {actionType === 'reject' && ' (Required for rejection)'}
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant={actionType === 'approve' ? 'success' : 'danger'}
            onClick={submitAction}
            disabled={processing || (actionType === 'reject' && !adminResponse.trim())}
          >
            {processing ? (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>
                Processing...
              </>
            ) : (
              <>
                <i className={`fas ${actionType === 'approve' ? 'fa-check' : 'fa-times'} me-2`}></i>
                {actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UpgradeManagement;